import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ordersAPI } from '../../services/storeClubAPI';
import ClubLayout from '../../components/StoreClub/ClubLayout';

interface OrderItem {
  id: number;
  product_name: string;
  quantity: number;
  unit_price: string;
  line_total: string;
}

interface LockerInfo {
  locker_number: number;
  locker_name: string;
  passcode: string | null;
  status: string;
}

interface OrderDetail {
  id: number;
  order_number: string;
  status: string;
  pickup_method: string;
  subtotal: string;
  convenience_fee: string;
  tax: string;
  total: string;
  locker_info: LockerInfo | null;
  items: OrderItem[];
  created_at: string;
  paid_at: string | null;
  filled_at: string | null;
  picked_up_at: string | null;
  notes?: string;
  payment_status?: string;
}

// Helper to format product names (convert ALL CAPS words to Title Case)
const formatProductName = (name: string): string => {
  return name.split(' ').map(word => {
    // If word is all uppercase and longer than 1 character, convert to title case
    if (word.length > 1 && word === word.toUpperCase() && /^[A-Z]+$/.test(word)) {
      return word.charAt(0) + word.slice(1).toLowerCase();
    }
    return word;
  }).join(' ');
};

const ClubOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNewOrder = searchParams.get('new') === 'true';
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadOrder();
  }, [id]);

  const loadOrder = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await ordersAPI.getOne(parseInt(id));
      setOrder(data);
    } catch (err: any) {
      console.error('Failed to load order:', err);
      setError(err.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'awaiting_payment': return '#ff9800';
      case 'paid': return '#2196f3';
      case 'ready_for_pickup': return '#4caf50';
      case 'picked_up': return '#9e9e9e';
      case 'cancelled': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return '🕐';
      case 'awaiting_payment': return '💳';
      case 'paid': return '⚙️';
      case 'ready_for_pickup': return '✅';
      case 'picked_up': return '📦';
      case 'cancelled': return '❌';
      default: return '📋';
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'pending': return 'Order Placed';
      case 'awaiting_payment': return 'Awaiting Payment';
      case 'paid': return 'Preparing';
      case 'ready_for_pickup': return 'Ready for Pickup';
      case 'picked_up': return 'Picked Up';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  if (loading) {
    return (
      <ClubLayout>
        <div style={styles.loading}>Loading order...</div>
      </ClubLayout>
    );
  }

  if (error || !order) {
    return (
      <ClubLayout>
        <div style={styles.error}>
          <div style={styles.errorIcon}>😕</div>
          <p style={styles.errorText}>{error || 'Order not found'}</p>
          <Link to="/club/orders" style={styles.backLink}>
            View All Orders
          </Link>
        </div>
      </ClubLayout>
    );
  }

  const showSuccessBanner = isNewOrder || order.status === 'pending';

  return (
    <ClubLayout>
      <div style={styles.content}>
        {/* Success Banner for new orders */}
        {showSuccessBanner && (
          <div style={styles.successBanner}>
            <div style={styles.successIcon}>🎉</div>
            <h2 style={styles.successTitle}>Order Placed!</h2>
            <p style={styles.successText}>
              Thank you for your order. We'll start preparing it soon.
            </p>
          </div>
        )}

        {/* Order Header */}
        <div style={styles.orderHeader}>
          <div style={styles.orderInfo}>
            <h2 style={styles.orderNumber}>Order #{order.order_number}</h2>
            <span style={styles.orderDate}>
              Placed {new Date(order.created_at).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
          </div>
          <div
            style={{
              ...styles.statusBadge,
              backgroundColor: getStatusColor(order.status),
            }}
          >
            {getStatusIcon(order.status)} {getStatusDisplay(order.status)}
          </div>
        </div>

        {/* Status Timeline */}
        <div style={styles.timeline}>
          <div style={{
            ...styles.timelineStep,
            ...(order.status !== 'cancelled' ? styles.timelineStepActive : {}),
          }}>
            <div style={styles.timelineIcon}>📝</div>
            <span style={styles.timelineLabel}>Placed</span>
          </div>
          <div style={styles.timelineLine} />
          <div style={{
            ...styles.timelineStep,
            ...((['paid', 'ready_for_pickup', 'picked_up'].includes(order.status)) ? styles.timelineStepActive : {}),
          }}>
            <div style={styles.timelineIcon}>⚙️</div>
            <span style={styles.timelineLabel}>Preparing</span>
          </div>
          <div style={styles.timelineLine} />
          <div style={{
            ...styles.timelineStep,
            ...((['ready_for_pickup', 'picked_up'].includes(order.status)) ? styles.timelineStepActive : {}),
          }}>
            <div style={styles.timelineIcon}>✅</div>
            <span style={styles.timelineLabel}>Ready</span>
          </div>
          <div style={styles.timelineLine} />
          <div style={{
            ...styles.timelineStep,
            ...(order.status === 'picked_up' ? styles.timelineStepActive : {}),
          }}>
            <div style={styles.timelineIcon}>🏠</div>
            <span style={styles.timelineLabel}>Complete</span>
          </div>
        </div>

        {/* Locker Info (if ready for locker pickup) */}
        {order.status === 'ready_for_pickup' && order.locker_info?.passcode && (
          <div style={styles.lockerCard}>
            <h3 style={styles.lockerTitle}>Your Locker is Ready!</h3>
            <div style={styles.lockerDetails}>
              <div style={styles.lockerItem}>
                <span style={styles.lockerItemLabel}>Locker</span>
                <span style={styles.lockerItemValue}>{order.locker_info.locker_name}</span>
              </div>
              <div style={styles.lockerItem}>
                <span style={styles.lockerItemLabel}>Access Code</span>
                <span style={styles.lockerCode}>{order.locker_info.passcode}</span>
              </div>
            </div>
            <p style={styles.lockerNote}>
              Your order is waiting for you at our store. Use the code above to unlock your locker.
            </p>
          </div>
        )}

        {/* Payment Instructions for awaiting payment orders */}
        {order.status === 'awaiting_payment' && order.pickup_method === 'locker' && (
          <div style={styles.paymentCard}>
            <div style={styles.paymentIcon}>💳</div>
            <h3 style={styles.paymentTitle}>Payment Required</h3>
            <p style={styles.paymentText}>
              Complete your payment to confirm your order. Once paid, we'll prepare your order for locker pickup.
            </p>
            <div style={styles.paymentAmount}>
              <span style={styles.paymentAmountLabel}>Amount Due</span>
              <span style={styles.paymentAmountValue}>${order.total}</span>
            </div>
            <Link to="/club/checkout" style={styles.payButton}>
              Complete Payment
            </Link>
          </div>
        )}

        {/* Pickup Method */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Pickup Method</h3>
          <div style={styles.pickupCard}>
            <span style={styles.pickupIcon}>
              {order.pickup_method === 'locker' ? '🔐' : '🏪'}
            </span>
            <span style={styles.pickupText}>
              {order.pickup_method === 'locker' ? 'Locker Pickup' : 'In-Store Pickup'}
            </span>
          </div>
        </div>

        {/* Order Items */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Items ({order.items.length})</h3>
          <div style={styles.itemsList}>
            {order.items.map((item) => (
              <div key={item.id} style={styles.itemRow}>
                <div style={styles.itemInfo}>
                  <span style={styles.itemQty}>{item.quantity}x</span>
                  <span style={styles.itemName}>{formatProductName(item.product_name)}</span>
                </div>
                <span style={styles.itemPrice}>${item.line_total}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Order Total */}
        <div style={styles.totalSection}>
          <div style={styles.totalRow}>
            <span>Subtotal</span>
            <span>${order.subtotal}</span>
          </div>
          {parseFloat(order.convenience_fee || '0') > 0 && (
            <div style={styles.totalRow}>
              <span>Convenience Fee (3%)</span>
              <span>${order.convenience_fee}</span>
            </div>
          )}
          {parseFloat(order.tax || '0') > 0 && (
            <div style={styles.totalRow}>
              <span>Tax</span>
              <span>${order.tax}</span>
            </div>
          )}
          <div style={styles.totalRowFinal}>
            <span>Total</span>
            <span style={styles.totalAmount}>${order.total}</span>
          </div>
        </div>

        {/* Notes */}
        {order.notes && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Order Notes</h3>
            <p style={styles.notes}>{order.notes}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div style={styles.actions}>
          <Link to="/club/orders" style={styles.secondaryButton}>
            View All Orders
          </Link>
          <Link to="/club/shop" style={styles.primaryButton}>
            Continue Shopping
          </Link>
        </div>
      </div>
    </ClubLayout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  content: {
    padding: '1rem',
    paddingBottom: '5rem',
    maxWidth: '600px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: 'calc(100vh - 120px)', // Ensure content area fills viewport
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
    color: '#666',
    fontSize: '1.125rem',
    padding: '2rem',
  },
  error: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    textAlign: 'center',
    minHeight: '50vh',
  },
  errorIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  errorText: {
    color: '#666',
    margin: '0 0 1rem 0',
  },
  backLink: {
    color: '#508590',
    textDecoration: 'none',
    fontWeight: 600,
  },
  successBanner: {
    backgroundColor: '#e8f5e9',
    borderRadius: '16px',
    padding: '1.5rem',
    textAlign: 'center',
    marginBottom: '1.5rem',
  },
  successIcon: {
    fontSize: '3rem',
    marginBottom: '0.5rem',
  },
  successTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#2e7d32',
    margin: '0 0 0.5rem 0',
  },
  successText: {
    color: '#388e3c',
    margin: 0,
    fontSize: '0.9375rem',
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  orderInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  orderNumber: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  orderDate: {
    fontSize: '0.8125rem',
    color: '#666',
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.5rem 0.75rem',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  timeline: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '0.75rem',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflowX: 'auto',
    flexWrap: 'nowrap',
    minWidth: 0, // Allows flex shrinking
  },
  timelineStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem',
    opacity: 0.4,
    flexShrink: 0,
    minWidth: '50px',
  },
  timelineStepActive: {
    opacity: 1,
  },
  timelineIcon: {
    fontSize: '1.125rem',
  },
  timelineLabel: {
    fontSize: '0.625rem',
    color: '#666',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  timelineLine: {
    flex: 1,
    height: '2px',
    backgroundColor: '#e0e0e0',
    margin: '0 0.25rem',
    minWidth: '10px',
  },
  lockerCard: {
    backgroundColor: '#e8f5e9',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    textAlign: 'center',
  },
  lockerTitle: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    color: '#2e7d32',
    margin: '0 0 1rem 0',
  },
  lockerDetails: {
    display: 'flex',
    justifyContent: 'center',
    gap: '2rem',
    marginBottom: '1rem',
  },
  lockerItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem',
  },
  lockerItemLabel: {
    fontSize: '0.75rem',
    color: '#388e3c',
  },
  lockerItemValue: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#2e7d32',
  },
  lockerCode: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#2e7d32',
    letterSpacing: '3px',
  },
  lockerNote: {
    fontSize: '0.8125rem',
    color: '#666',
    margin: 0,
  },
  paymentCard: {
    backgroundColor: '#fff3e0',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    textAlign: 'center',
    border: '2px solid #ffcc80',
  },
  paymentIcon: {
    fontSize: '2.5rem',
    marginBottom: '0.5rem',
  },
  paymentTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#e65100',
    margin: '0 0 0.5rem 0',
  },
  paymentText: {
    color: '#666',
    margin: '0 0 1rem 0',
    fontSize: '0.9375rem',
    lineHeight: '1.5',
  },
  paymentAmount: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem',
    marginBottom: '1rem',
    padding: '0.75rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
  },
  paymentAmountLabel: {
    fontSize: '0.75rem',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  paymentAmountValue: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#e65100',
  },
  payButton: {
    display: 'inline-block',
    backgroundColor: '#ff9800',
    color: '#fff',
    padding: '0.875rem 2rem',
    borderRadius: '12px',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    boxShadow: '0 2px 4px rgba(255, 152, 0, 0.3)',
  },
  paymentPending: {
    color: '#888',
    fontSize: '0.875rem',
    margin: 0,
    fontStyle: 'italic',
  },
  section: {
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#333',
    margin: '0 0 0.75rem 0',
  },
  pickupCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  pickupIcon: {
    fontSize: '1.5rem',
  },
  pickupText: {
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: '#333',
  },
  itemsList: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.875rem 1rem',
    borderBottom: '1px solid #f0f0f0',
  },
  itemInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  itemQty: {
    fontSize: '0.875rem',
    color: '#666',
    fontWeight: 500,
  },
  itemName: {
    fontSize: '0.9375rem',
    color: '#333',
  },
  itemPrice: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: '#333',
  },
  totalSection: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1rem',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    fontSize: '0.9375rem',
    color: '#666',
  },
  totalRowFinal: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 0 0 0',
    marginTop: '0.5rem',
    borderTop: '1px solid #e0e0e0',
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#333',
  },
  totalAmount: {
    color: '#E73C3E',
  },
  notes: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1rem',
    margin: 0,
    fontSize: '0.9375rem',
    color: '#666',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '2rem',
  },
  primaryButton: {
    flex: 1,
    backgroundColor: '#508590',
    color: '#fff',
    padding: '0.875rem',
    borderRadius: '12px',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '0.9375rem',
    textAlign: 'center',
  },
  secondaryButton: {
    flex: 1,
    backgroundColor: '#fff',
    color: '#508590',
    padding: '0.875rem',
    borderRadius: '12px',
    border: '2px solid #508590',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '0.9375rem',
    textAlign: 'center',
  },
};

export default ClubOrderDetail;
